import { crossFieldIssues, formSchema, STEP_FIELDS } from '../../shared/schema'
import { setStatus } from './status'

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

function isFormField(el: Element | RadioNodeList | EventTarget): el is FormField {
  return el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement
}

/** Read every named field into a plain object (checkboxes as booleans). */
export function collectFormValues(form: HTMLFormElement): Record<string, unknown> {
  const data: Record<string, unknown> = {}
  for (const el of Array.from(form.elements)) {
    if (!isFormField(el) || !el.name) continue
    data[el.name] = el instanceof HTMLInputElement && el.type === 'checkbox' ? el.checked : el.value
  }
  return data
}

// ---------------------------------------------------------------------------
// Inline error rendering (aria-wired)
// ---------------------------------------------------------------------------

function errorId(field: FormField): string {
  return `${field.id || field.name}-error`
}

function setFieldError(field: FormField, message: string): void {
  field.setAttribute('aria-invalid', 'true')
  let err = document.getElementById(errorId(field))
  if (!err) {
    err = document.createElement('span')
    err.className = 'field-error'
    err.id = errorId(field)
    field.insertAdjacentElement('afterend', err)
  }
  err.textContent = message

  const ids = (field.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean)
  if (!ids.includes(err.id)) {
    field.setAttribute('aria-describedby', [...ids, err.id].join(' '))
  }
}

export function clearFieldError(field: FormField): void {
  field.removeAttribute('aria-invalid')
  const err = document.getElementById(errorId(field))
  if (err) {
    const ids = (field.getAttribute('aria-describedby') ?? '')
      .split(/\s+/)
      .filter((id) => id && id !== err.id)
    if (ids.length) field.setAttribute('aria-describedby', ids.join(' '))
    else field.removeAttribute('aria-describedby')
    err.remove()
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Parse the whole form and return the first issue message per field,
 * restricted to `scope`. Empty map = everything in scope is valid.
 */
function firstIssuePerField(form: HTMLFormElement, scope: readonly string[]): Map<string, string> {
  const messages = new Map<string, string>()
  const values = collectFormValues(form)

  const result = formSchema.safeParse(values)
  if (!result.success) {
    for (const issue of result.error.issues) {
      const name = String(issue.path[0])
      // First message per field wins (don't overwrite with later issues).
      if (scope.includes(name) && !messages.has(name)) messages.set(name, issue.message)
    }
  }

  // Zod skips superRefine while any base field fails to parse — the norm
  // mid-form — so evaluate the cross-field rules directly too. The Map
  // dedupes when the parse did reach them.
  for (const { path, message } of crossFieldIssues(values)) {
    if (scope.includes(path) && !messages.has(path)) messages.set(path, message)
  }
  return messages
}

/**
 * Validate the whole form, but render/report errors only for `fieldNames`
 * (null = all fields). Returns true when those fields are all valid.
 */
function validateFields(form: HTMLFormElement, fieldNames: readonly string[] | null): boolean {
  const scope = fieldNames ?? STEP_FIELDS.flat()

  // Clear previous errors within scope so messages don't go stale.
  for (const name of scope) {
    const field = form.elements.namedItem(name)
    if (field && isFormField(field)) clearFieldError(field)
  }

  const issues = firstIssuePerField(form, scope)
  if (issues.size === 0) {
    setStatus('')
    return true
  }

  for (const [name, message] of issues) {
    const field = form.elements.namedItem(name)
    if (field && isFormField(field)) setFieldError(field, message)
  }

  // Focus the first invalid field in DOM order.
  const firstInvalid = form.querySelector<FormField>('[aria-invalid="true"]')
  firstInvalid?.focus()

  const n = issues.size
  setStatus(`Please fix the ${n} highlighted ${n === 1 ? 'field' : 'fields'} before continuing.`, true)
  return false
}

/**
 * Validate one field on blur/edit: renders or clears only its own error.
 * No focus steal, no status banner.
 */
function validateSingleField(form: HTMLFormElement, field: FormField): boolean {
  if (!field.name || field.closest('.hidden')) return true
  const message = firstIssuePerField(form, [field.name]).get(field.name)
  if (message) setFieldError(field, message)
  else clearFieldError(field)
  return !message
}

export function validateStep(form: HTMLFormElement, stepIndex: number): boolean {
  return validateFields(form, STEP_FIELDS[stepIndex] ?? null)
}

export function validateAll(form: HTMLFormElement): boolean {
  return validateFields(form, null)
}

/**
 * Render field errors returned by the server (authoritative validation).
 * Rare in practice — the client validates with the same schema first.
 */
export function applyServerFieldErrors(form: HTMLFormElement, fieldErrors: Record<string, string>): boolean {
  let applied = false
  for (const [name, message] of Object.entries(fieldErrors)) {
    const field = form.elements.namedItem(name)
    if (field && isFormField(field)) {
      setFieldError(field, message)
      applied = true
    }
  }
  form.querySelector<FormField>('[aria-invalid="true"]')?.focus()
  return applied
}

/**
 * Material-style live validation: a field validates whenever the user leaves
 * it (touched semantics — a required field errors on blur even if nothing was
 * typed), and a field already showing an error re-validates on every edit so
 * the message clears the moment the value becomes valid.
 */
export function initValidation(form: HTMLFormElement): void {
  const onEdit = (e: Event) => {
    const target = e.target as Element
    if (isFormField(target) && target.getAttribute('aria-invalid') === 'true') {
      validateSingleField(form, target)
    }
  }
  form.addEventListener('input', onEdit)
  form.addEventListener('change', onEdit)

  form.addEventListener('focusout', (e: FocusEvent) => {
    const target = e.target as Element
    if (!isFormField(target)) return
    // Leaving for Next/Back/Submit: those run their own step validation, and
    // inserting an error span mid-click would shift the buttons under the cursor.
    if (e.relatedTarget instanceof Element && e.relatedTarget.closest('.button-row')) return
    validateSingleField(form, target)
  })
}
